/**
 * 입력 파라미터와 원단 프리셋.
 * 새틴 간격(density) = 한쪽 가장자리의 바늘땀 사이 거리(peak-to-peak, mm). 0.40 이 표준.
 * 풀보정(pullComp) = 새틴 폭을 양쪽으로 각각 늘리는 양(mm).
 */
export type UnderlayMode = "auto" | "center" | "edge" | "zigzag" | "edge+zigzag";
/** 작은 글자 방식: auto = 6.5mm 미만이면 러닝 */
export type SmallStyle = "auto" | "satin" | "run";
export type Align = "left" | "center" | "right";
export type FabricId = "tshirt" | "pique" | "towel" | "cap" | "denim";

export interface FabricPreset {
  id: FabricId;
  name: string;
  short: string;
  density: number;
  pullComp: number;
  underlay: UnderlayMode;
  /** 넓은 새틴에 지그재그 언더레이를 더할 폭(mm) */
  zigzagFrom: number;
  /** 모자: 가운데 글자부터 바깥으로 */
  centerOut: boolean;
  /** 권장 최소 글자 높이 mm */
  minHeight: number;
  note: string;
}

export const FABRICS: Record<FabricId, FabricPreset> = {
  tshirt: {
    id: "tshirt",
    name: "면 티셔츠 (니트)",
    short: "티셔츠",
    density: 0.42,
    pullComp: 0.25,
    underlay: "auto",
    zigzagFrom: 2.2,
    centerOut: false,
    minHeight: 7,
    note: "잘 늘어나는 원단입니다. 뒷면에 커트어웨이 심지를 대고, 밀도를 조금 성기게, 풀보정을 크게 잡았습니다.",
  },
  pique: {
    id: "pique",
    name: "피케 (카라티)",
    short: "피케",
    density: 0.4,
    pullComp: 0.25,
    underlay: "edge+zigzag",
    zigzagFrom: 1.8,
    centerOut: false,
    minHeight: 6,
    note: "표면 요철에 실이 묻히지 않도록 언더레이를 두껍게 깔았습니다. 얇은 수용성 토핑을 쓰면 더 선명합니다.",
  },
  towel: {
    id: "towel",
    name: "수건 (테리)",
    short: "수건",
    density: 0.37,
    pullComp: 0.2,
    underlay: "edge+zigzag",
    zigzagFrom: 1.4,
    centerOut: false,
    minHeight: 10,
    note: "파일(고리) 사이로 실이 가라앉지 않게 언더레이와 밀도를 높였습니다. 수용성 토핑을 꼭 올려 주세요. 작은 글자는 권하지 않습니다.",
  },
  cap: {
    id: "cap",
    name: "모자 (앞판)",
    short: "모자",
    density: 0.4,
    pullComp: 0.18,
    underlay: "auto",
    zigzagFrom: 2.5,
    centerOut: true,
    minHeight: 6,
    note: "가운데 글자부터 바깥쪽으로 박도록 순서를 잡았습니다(모자 틀어짐 방지). 모자 틀 기준 높이 5.5cm 이내를 권장합니다.",
  },
  denim: {
    id: "denim",
    name: "데님·면 트윌 (두꺼운 직물)",
    short: "데님",
    density: 0.4,
    pullComp: 0.15,
    underlay: "auto",
    zigzagFrom: 3,
    centerOut: false,
    minHeight: 5,
    note: "잘 늘어나지 않는 원단이라 풀보정을 작게 잡았습니다. 티어어웨이 심지로 충분합니다.",
  },
};

export const FABRIC_ORDER: FabricId[] = ["tshirt", "pique", "towel", "cap", "denim"];

export interface StitchParams {
  heightMm: number;
  letterSpacingMm: number;
  /** 줄 간격 = 글자 높이 × (1 + lineGap) */
  lineGap: number;
  align: Align;
  fabric: FabricId;
  density: number;
  pullComp: number;
  underlay: UnderlayMode;
  smallStyle: SmallStyle;
  // 이하는 고급(보통 기본값)
  satinMax: number;
  fillThreshold: number;
  fillSpacing: number;
  fillStitch: number;
  fillAngle: number;
  overlap: number;
  minStitch: number;
  runStitch: number;
  tieLen: number;
  /** 글자 안에서 이보다 긴 점프는 트림 */
  trimJumpMm: number;
}

export function defaultParams(fabric: FabricId = "tshirt", heightMm = 15): StitchParams {
  const f = FABRICS[fabric];
  return {
    heightMm,
    letterSpacingMm: 0.5,
    lineGap: 0.45,
    align: "center",
    fabric,
    density: f.density,
    pullComp: f.pullComp,
    underlay: f.underlay,
    smallStyle: "auto",
    satinMax: 7,
    fillThreshold: 7.5,
    fillSpacing: 0.4,
    fillStitch: 3.5,
    fillAngle: 45,
    overlap: 0.35,
    minStitch: 0.3,
    runStitch: 2.0,
    tieLen: 0.6,
    trimJumpMm: 2,
  };
}

export const LIMITS = {
  heightMm: [5, 50],
  letterSpacingMm: [-3, 15],
  lineGap: [0, 2],
  density: [0.3, 0.6],
  pullComp: [0, 0.5],
  satinMax: [3, 10],
  fillThreshold: [4, 12],
  trimJumpMm: [0.5, 20],
} as const;

const clampNum = (v: unknown, lo: number, hi: number, dflt: number) => {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return dflt;
  return Math.min(hi, Math.max(lo, n));
};

/** 외부 입력을 안전한 범위로 */
export function sanitizeParams(p: Partial<StitchParams>): StitchParams {
  const fabric: FabricId = p.fabric && p.fabric in FABRICS ? p.fabric : "tshirt";
  const d = defaultParams(fabric, 15);
  const underlays: UnderlayMode[] = ["auto", "center", "edge", "zigzag", "edge+zigzag"];
  return {
    ...d,
    heightMm: clampNum(p.heightMm, 5, 50, 15),
    letterSpacingMm: clampNum(p.letterSpacingMm, -3, 15, d.letterSpacingMm),
    lineGap: clampNum(p.lineGap, 0, 2, d.lineGap),
    align: p.align === "left" || p.align === "right" ? p.align : "center",
    density: clampNum(p.density, 0.3, 0.6, d.density),
    pullComp: clampNum(p.pullComp, 0, 0.5, d.pullComp),
    underlay: p.underlay && underlays.includes(p.underlay) ? p.underlay : d.underlay,
    smallStyle: p.smallStyle === "satin" || p.smallStyle === "run" ? p.smallStyle : "auto",
    satinMax: clampNum(p.satinMax, 3, 10, d.satinMax),
    fillThreshold: clampNum(p.fillThreshold, 4, 12, d.fillThreshold),
    fillAngle: clampNum(p.fillAngle, -90, 90, d.fillAngle),
    trimJumpMm: clampNum(p.trimJumpMm, 0.5, 20, d.trimJumpMm),
  };
}
